import * as yup from "yup";
import StringSchema from "yup/lib/string";

import { countriesDialCodesLength } from "../../shared";
import {
  requiredMessage,
  strictLengthMessage,
  minLengthMessage,
  maxLengthMessage,
} from "../validationMessages";

import { trimmedString } from "./trimmedString";

const PREFIX_LENGTH = 1;

type SchemaType = StringSchema;

type CountryDetails = {
  country: string;
  isActive: boolean;
};

const genericPhoneValidator = (
  schema: SchemaType,
  charLength: number
): SchemaType =>
  schema.length(charLength + PREFIX_LENGTH, strictLengthMessage(charLength));

const genericPhoneValidatorRange = (
  schema: SchemaType,
  charLength: number
): SchemaType =>
  schema
    .min(
      charLength + 9 + PREFIX_LENGTH,
      minLengthMessage(charLength + 9 + PREFIX_LENGTH)
    )
    .max(
      charLength + 15 + PREFIX_LENGTH,
      maxLengthMessage(charLength + 15 + PREFIX_LENGTH)
    );

const phoneRequiredValidatorsRange = (
  schema: SchemaType
): Record<string, SchemaType> => ({
  ad: genericPhoneValidatorRange(schema, 3),
  ba: genericPhoneValidatorRange(schema, 3),
  at: genericPhoneValidatorRange(schema, 2),
  be: genericPhoneValidatorRange(schema, 2),
  bg: genericPhoneValidatorRange(schema, 3),
  by: genericPhoneValidatorRange(schema, 3),
  ca: genericPhoneValidatorRange(schema, 1),
  cn: genericPhoneValidatorRange(schema, 2),
  cy: genericPhoneValidatorRange(schema, 3),
  cz: genericPhoneValidatorRange(schema, 3),
  de: genericPhoneValidatorRange(schema, 2),
  dk: genericPhoneValidatorRange(schema, 2),
  ee: genericPhoneValidatorRange(schema, 3),
  es: genericPhoneValidatorRange(schema, 2),
  fi: genericPhoneValidatorRange(schema, 3),
  fr: genericPhoneValidatorRange(schema, 2),
  gb: genericPhoneValidatorRange(schema, 2),
  gr: genericPhoneValidatorRange(schema, 2),
  hr: genericPhoneValidatorRange(schema, 3),
  hu: genericPhoneValidatorRange(schema, 2),
  ie: genericPhoneValidatorRange(schema, 3),
  is: genericPhoneValidatorRange(schema, 3),
  it: genericPhoneValidatorRange(schema, 2),
  li: genericPhoneValidatorRange(schema, 3),
  lt: genericPhoneValidatorRange(schema, 3),
  lu: genericPhoneValidatorRange(schema, 3),
  lv: genericPhoneValidatorRange(schema, 3),
  ma: genericPhoneValidatorRange(schema, 3),
  mc: genericPhoneValidatorRange(schema, 3),
  md: genericPhoneValidatorRange(schema, 3),
  mk: genericPhoneValidatorRange(schema, 3),
  nl: genericPhoneValidatorRange(schema, 2),
  no: genericPhoneValidatorRange(schema, 2),
  pa: genericPhoneValidatorRange(schema, 3),
  pl: genericPhoneValidatorRange(schema, 2),
  pt: genericPhoneValidatorRange(schema, 3),
  ro: genericPhoneValidatorRange(schema, 2),
  ru: genericPhoneValidatorRange(schema, 1),
  se: genericPhoneValidatorRange(schema, 2),
  si: genericPhoneValidatorRange(schema, 3),
  sk: genericPhoneValidatorRange(schema, 3),
  sm: genericPhoneValidatorRange(schema, 3),
  sz: genericPhoneValidatorRange(schema, 3),
  tr: genericPhoneValidatorRange(schema, 2),
  ua: genericPhoneValidatorRange(schema, 3),
  us: genericPhoneValidatorRange(schema, 1),
  va: genericPhoneValidatorRange(schema, 2),
  xk: genericPhoneValidatorRange(schema, 3),
  in: genericPhoneValidatorRange(schema, 2),
});

const phoneRequiredValidators = (
  schema: SchemaType
): Record<string, SchemaType> => ({
  ad: genericPhoneValidator(schema, 15),
  at: genericPhoneValidator(schema, 15),
  ba: genericPhoneValidator(schema, 15),
  be: genericPhoneValidator(schema, 11),
  bg: genericPhoneValidator(schema, 15),
  by: genericPhoneValidator(schema, 12),
  ca: genericPhoneValidator(schema, 11),
  cn: genericPhoneValidator(schema, 13),
  cy: genericPhoneValidator(schema, 11),
  cz: genericPhoneValidator(schema, 15),
  de: genericPhoneValidator(schema, 14),
  dk: genericPhoneValidator(schema, 10),
  ee: genericPhoneValidator(schema, 13),
  es: genericPhoneValidator(schema, 11),
  fi: genericPhoneValidator(schema, 12),
  fr: genericPhoneValidator(schema, 11),
  gb: genericPhoneValidator(schema, 12),
  gr: genericPhoneValidator(schema, 15),
  hr: genericPhoneValidator(schema, 15),
  hu: genericPhoneValidator(schema, 15),
  ie: genericPhoneValidator(schema, 12),
  is: genericPhoneValidator(schema, 10),
  it: genericPhoneValidator(schema, 12),
  li: genericPhoneValidator(schema, 15),
  lt: genericPhoneValidator(schema, 15),
  lu: genericPhoneValidator(schema, 15),
  lv: genericPhoneValidator(schema, 15),
  ma: genericPhoneValidator(schema, 15),
  mc: genericPhoneValidator(schema, 15),
  md: genericPhoneValidator(schema, 11),
  mk: genericPhoneValidator(schema, 15),
  nl: genericPhoneValidator(schema, 12),
  no: genericPhoneValidator(schema, 10),
  pa: genericPhoneValidator(schema, 15),
  pl: genericPhoneValidator(schema, 11),
  pt: genericPhoneValidator(schema, 15),
  ro: genericPhoneValidator(schema, 15),
  ru: genericPhoneValidator(schema, 11),
  se: genericPhoneValidator(schema, 11),
  si: genericPhoneValidator(schema, 15),
  sk: genericPhoneValidator(schema, 15),
  sm: genericPhoneValidator(schema, 15),
  sz: genericPhoneValidator(schema, 15),
  tr: genericPhoneValidator(schema, 12),
  ua: genericPhoneValidator(schema, 12),
  us: genericPhoneValidator(schema, 11),
  va: genericPhoneValidator(schema, 12),
  xk: genericPhoneValidator(schema, 15),
  in: genericPhoneValidator(schema, 12),
});

export const phoneValidatorRequired = (field: string): SchemaType =>
  trimmedString.when(
    `$${field}`,
    (countryDetails: CountryDetails, schema: SchemaType) =>
      countryDetails?.isActive
        ? phoneRequiredValidatorsRange(schema)[countryDetails.country].required(
            requiredMessage
          )
        : yup.string().notRequired()
  );

export const phoneValidator = (field: string): SchemaType =>
  trimmedString.when(`$${field}`, (countryDetails: CountryDetails) => {
    if (!countryDetails?.isActive) {
      return yup.string().notRequired();
    }

    return yup.lazy((val: string) => {
      const shouldValidate =
        val.length - countriesDialCodesLength[countryDetails.country] > 0;

      if (!shouldValidate) {
        return yup.string().notRequired();
      }

      return phoneValidatorRequired(field);
    });
  });
